import type { Prediction } from './types'

export type ScoreOutcome = 'H' | 'D' | 'A' // home win, draw, away win

function getOutcome(homeScore: number, awayScore: number): ScoreOutcome {
  if (homeScore > awayScore) return 'H'
  if (homeScore < awayScore) return 'A'
  return 'D'
}

/**
 * Whether a predicted score matches the actual result's outcome (H/D/A).
 * An exact score match is by definition also a correct outcome.
 */
export function isCorrectOutcome(
  predictedHome: number,
  predictedAway: number,
  actualHome: number,
  actualAway: number
): boolean {
  return (
    getOutcome(predictedHome, predictedAway) === getOutcome(actualHome, actualAway)
  )
}

export interface PointsResult {
  points: number
  reason: string
}

/** What an auto-filled pick pays for calling the outcome. Nothing otherwise. */
export const AUTO_PICK_POINTS = 2

/**
 * Scores a single game prediction against the actual result.
 * Rules:
 *  - Auto-filled pick: 2 points for the right outcome, 0 for anything else
 *  - Exact score: 5 points
 *  - Correct outcome (win/draw/loss), wrong score: 3 points
 *  - Wrong outcome: 0 base points, +1 for each team's goal count
 *    correctly predicted. Note: in practice this bonus maxes out at +1,
 *    since matching both teams' goals exactly would already count as
 *    an exact score above.
 *
 * The auto-pick branch is the single place the cap is applied, and it answers the whole
 * question rather than falling through — a fall-through is exactly how partial credit for a
 * pick nobody made would creep back in.
 */
export function calculateGamePoints(
  predictedHome: number,
  predictedAway: number,
  actualHome: number,
  actualAway: number,
  isAutoPredicted = false
): PointsResult {
  if (isAutoPredicted) {
    const correct = isCorrectOutcome(predictedHome, predictedAway, actualHome, actualAway)
    return { points: correct ? AUTO_PICK_POINTS : 0, reason: 'Auto pick' }
  }

  const isExact = predictedHome === actualHome && predictedAway === actualAway
  if (isExact) {
    return { points: 5, reason: 'Exact score' }
  }

  const predictedOutcome = getOutcome(predictedHome, predictedAway)
  const actualOutcome = getOutcome(actualHome, actualAway)

  if (predictedOutcome === actualOutcome) {
    return { points: 3, reason: 'Correct outcome' }
  }

  const homeGoalsCorrect = predictedHome === actualHome
  const awayGoalsCorrect = predictedAway === actualAway
  const bonus = (homeGoalsCorrect ? 1 : 0) + (awayGoalsCorrect ? 1 : 0)

  if (bonus > 0) {
    return { points: bonus, reason: `${bonus} correct goal${bonus > 1 ? 's' : ''} predicted` }
  }

  return { points: 0, reason: 'No points' }
}

/**
 * `calculateGamePoints` for callers holding a `Prediction` — which is all of them. Preferred
 * over the bare-numbers form because it cannot forget to pass the auto-pick flag.
 */
export function scorePrediction(
  prediction: Prediction,
  actual: { homeScore: number; awayScore: number }
): PointsResult {
  return calculateGamePoints(
    prediction.homeScore,
    prediction.awayScore,
    actual.homeScore,
    actual.awayScore,
    prediction.isAutoPredicted ?? false
  )
}

export function applyMultipliers(
    basePoints: number,
    isBanker: boolean,
    isDoubleUpActive: boolean
  ): number {
    let multiplier = 1
    if (isBanker) multiplier *= 2
    if (isDoubleUpActive) multiplier *= 2
    return basePoints * multiplier
  }

export const SEASON_TOTAL_GAMES = 380